import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { getAttendanceDailyDiagnostics, getAttendanceEmployeePunches, listAttendanceInOutReport, listAttendanceOutlets } from '../../services/attendanceSyncApi';
import PaginationBar from './PaginationBar';
import { formatDate, formatDateTime, todayDate } from './dateTime';
import './AttendanceInOutPage.css';

const statusLabel = (status) => {
  if (status === 'in_progress') return 'In Progress';
  if (status === 'missing_out') return 'Missing Out';
  return String(status || '-').replace('_', ' ');
};

function AttendanceInOutPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ date: todayDate(), search: '', outletId: '', status: 'all', page: 1, limit: 25 });
  const [searchInput, setSearchInput] = useState('');
  const [outlets, setOutlets] = useState([]);
  const requestSeq = useRef(0);
  const punchRequestSeq = useRef(0);
  const mounted = useRef(true);
  const [data, setData] = useState({ rows: [], pagination: {}, summary: {} });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [diagnostics, setDiagnostics] = useState(null);
  const [punchModal, setPunchModal] = useState(null);
  const [punches, setPunches] = useState([]);
  const [punchLoading, setPunchLoading] = useState(false);
  const [punchError, setPunchError] = useState('');

  useEffect(() => () => { mounted.current = false; punchRequestSeq.current += 1; }, []);

  const loadReport = async (nextFilters = filters) => {
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setLoading(true);
    try {
      const [reportRes, diagnosticsRes] = await Promise.all([
        listAttendanceInOutReport(nextFilters),
        getAttendanceDailyDiagnostics({ date: nextFilters.date }),
      ]);
      if (requestSeq.current === seq) {
        setData(reportRes.data || { rows: [], pagination: {}, summary: {} });
        setDiagnostics(diagnosticsRes.data || null);
      }
    } catch (e) { if (requestSeq.current === seq) setMessage(e.message); }
    finally { if (requestSeq.current === seq) setLoading(false); }
  };

  useEffect(() => { loadAttendanceOutlets(); }, []);
  useEffect(() => { loadReport(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters]);
  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters((f) => (f.search === searchInput ? f : { ...f, search: searchInput, page: 1 }));
    }, 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const loadAttendanceOutlets = async () => {
    try {
      const res = await listAttendanceOutlets();
      setOutlets(Array.isArray(res.data) ? res.data : []);
    } catch (e) { setMessage(e.message); }
  };
  const change = (patch) => setFilters((f) => ({ ...f, ...patch, page: 1 }));
  const changePage = (page) => setFilters((f) => ({ ...f, page }));
  const changeLimit = (limit) => setFilters((f) => ({ ...f, limit, page: 1 }));
  const openPunches = async (row) => {
    if (loading || punchLoading) return;
    const seq = ++punchRequestSeq.current;
    setPunchModal(row); setPunches([]); setPunchError(''); setPunchLoading(true);
    try {
      const res = await getAttendanceEmployeePunches({ employeeId: row.employeeId, date: filters.date });
      if (mounted.current && punchRequestSeq.current === seq) setPunches(res.data || []);
    } catch (e) { if (mounted.current && punchRequestSeq.current === seq) { setPunches([]); setPunchError(e.message || 'Unable to load punch details.'); } }
    finally { if (mounted.current && punchRequestSeq.current === seq) setPunchLoading(false); }
  };
  const closePunches = () => { punchRequestSeq.current += 1; setPunchModal(null); setPunches([]); setPunchError(''); setPunchLoading(false); };

  const summary = data.summary || {};
  const diagnosticWarnings = [];
  if (diagnostics) {
    if (Number(diagnostics.rawLogsTotal || 0) === 0) diagnosticWarnings.push('No ESSL logs synced for selected date.');
    if (Number(diagnostics.rawLogsTotal || 0) > 0 && Number(diagnostics.linkedEmployees || 0) === 0) diagnosticWarnings.push('ESSL logs synced but employees are not mapped.');
    if (Number(diagnostics.unlinkedPunches || 0) > 0) diagnosticWarnings.push(`${diagnostics.unlinkedPunches} unlinked punches found for selected date.`);
  }
  const cards = [
    ['Total Employees', summary.totalEmployees],
    ['Present', summary.presentCount],
    ['In Progress', summary.inProgressCount],
    ['Missing Out', summary.missingOutCount],
    ['Absent', summary.absentCount],
    ['Valid Punches', summary.totalPunches],
  ];

  return (
    <DashboardLayout>
      <div className="attendance-page">
        <div className="attendance-header"><h1>Attendance In/Out</h1><button className="att-btn" disabled={loading} onClick={() => loadReport()}>Refresh</button></div>
        {message && <div className="att-alert">{message}</div>}
        {diagnosticWarnings.map((warning) => <div className="att-alert warning" key={warning}>{warning}</div>)}
        <div className="att-filters">
          <input type="date" value={filters.date} onChange={(e) => change({ date: e.target.value })} />
          <input placeholder="Search code, name, or phone" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
          <select value={filters.outletId} onChange={(e) => change({ outletId: e.target.value })}><option value="">All outlets</option>{outlets.map((outlet)=><option key={outlet.id} value={outlet.id}>{outlet.branchName || outlet.branch_name || outlet.name || `Outlet #${outlet.id}`}</option>)}</select>
          <select value={filters.status} onChange={(e) => change({ status: e.target.value })}>
            <option value="all">All</option><option value="present">Present</option><option value="in_progress">In Progress</option><option value="missing_out">Missing Out</option><option value="absent">Absent</option>
          </select>
        </div>
        <div className="attendance-cards">{cards.map(([k,v])=><div className="att-card" key={k}><span>{k}</span><strong>{v ?? 0}</strong></div>)}</div>
        <div className="att-panel">
          {loading && <div className="att-loading">Loading...</div>}
          <div className="att-table-wrapper"><table className="att-table"><thead><tr><th>Temporary ID</th><th>Permanent ID</th><th>Employee Name</th><th>Outlet</th><th>Designation</th><th>Date</th><th>In Time</th><th>Out Time</th><th>Raw Punches</th><th>Valid Punches</th><th>Ignored</th><th>Status</th><th>Action</th></tr></thead><tbody>{(data.rows||[]).length ? (data.rows||[]).map((row)=><tr key={row.employeeId}><td>{row.tempEmployeeId || '-'}</td><td>{row.permanentEmployeeId || '-'}</td><td>{row.employeeName || '-'}</td><td>{row.outletName || '-'}</td><td>{row.designationName || '-'}</td><td>{formatDate(row.attendanceDate || filters.date)}</td><td>{formatDateTime(row.inTime || row.in_time)}</td><td>{formatDateTime(row.outTime || row.out_time)}</td><td>{row.rawPunchCount ?? row.raw_punch_count ?? row.punchCount ?? row.punch_count ?? 0}</td><td>{row.validPunchCount ?? row.valid_punch_count ?? row.punchCount ?? row.punch_count ?? 0}</td><td>{row.ignoredPunchCount ?? row.ignored_punch_count ?? 0}</td><td><span className={`att-status-badge att-status-${row.attendanceStatus}`}>{statusLabel(row.attendanceStatus)}</span></td><td><button className="att-btn primary" disabled={loading || punchLoading} onClick={()=>openPunches(row)}>View Punches</button> <button className="att-btn" onClick={() => navigate(`/attendance-calendar/${row.employeeId}`)}>Monthly Calendar</button></td></tr>) : <tr><td colSpan="13">No records found</td></tr>}</tbody></table></div>
          <PaginationBar pagination={data.pagination} loading={loading} onPageChange={changePage} onLimitChange={changeLimit} />
        </div>
        {punchModal && <div className="att-modal-backdrop"><div className="att-modal"><div className="att-modal-header"><div><h2>Employee Punches</h2><p>{punchModal.permanentEmployeeId || punchModal.tempEmployeeId || '-'} - {punchModal.employeeName} ({filters.date})</p></div><button className="att-btn" onClick={closePunches}>Close</button></div>{punchLoading && <div className="att-loading">Loading punch details…</div>}{punchError && <div className="att-alert"><p>{punchError}</p><button className="att-btn" onClick={() => openPunches(punchModal)}>Retry</button></div>}{!punchLoading && !punchError && <div className="att-table-wrapper"><table className="att-table"><thead><tr><th>Punch No</th><th>Punch Time (Asia/Kolkata)</th><th>Type</th><th>Status</th><th>Device ID</th><th>Source User ID</th></tr></thead><tbody>{punches.length ? punches.map((p)=><tr key={p.punchNo}><td>{p.punchNo}</td><td>{formatDateTime(p.punchTime)}</td><td>{p.punchType}</td><td>{p.punchValidity || (p.isIgnored ? 'Ignored' : 'Valid')}</td><td>{p.sourceDeviceId || '-'}</td><td>{p.sourceUserId || '-'}</td></tr>) : <tr><td colSpan="6">No punches found for this date.</td></tr>}</tbody></table></div>}</div></div>}
      </div>
    </DashboardLayout>
  );
}

export default AttendanceInOutPage;
